import React, { useState } from "react";
import { gql } from "@apollo/client";
import { toast } from "sonner";
import { Button } from "@src/lib/ui/Button";
import { Textarea } from "@src/lib/ui/Textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@src/lib/ui/Tooltip";
import Text from "@src/lib/ui/Text";
import ThumbsUpOutlineIcon from "@src/lib/icons/18px/thumbs-up-outline";
import ThumbsDownOutlineIcon from "@src/lib/icons/18px/thumbs-down-outline";
import { cn } from "@src/lib/utils/cn";
import { FeedbackSelection } from "@src/types.generated";
import { useSetOpusAgentTurnFeedbackMutation } from "./OpusAgentTurnFeedback.generated";
import type { OpusAgentTurnFragment } from "./useOpusAgentChat.generated";

type Feedback = NonNullable<OpusAgentTurnFragment["myFeedback"]>;

interface OpusAgentTurnFeedbackProps {
  turnId: string;
  initialFeedback?: OpusAgentTurnFragment["myFeedback"];
}

// Thumbs up / down under a finished answer. A click saves the rating at once;
// thumbs down then offers an optional note, saved as a second write of the
// same row. Rating again (switching thumbs) replaces the row server-side.
export const OpusAgentTurnFeedback: React.FC<OpusAgentTurnFeedbackProps> = ({
  turnId,
  initialFeedback,
}) => {
  const [setFeedback, { loading }] = useSetOpusAgentTurnFeedbackMutation();
  const [selection, setSelection] = useState<FeedbackSelection | null>(
    initialFeedback?.selection ?? null,
  );
  const [askingForNote, setAskingForNote] = useState(false);
  const [note, setNote] = useState("");
  const [thanked, setThanked] = useState(false);

  const save = async (next: FeedbackSelection, text?: string) => {
    const result = await setFeedback({
      variables: { input: { turnId, selection: next, text } },
    }).catch(() => null);
    const saved = result?.data?.setOpusAgentTurnFeedback;
    if (!saved?.success) {
      toast.error(saved?.error?.message ?? "Couldn't save your feedback.");
      return false;
    }
    return true;
  };

  const rate = async (next: FeedbackSelection) => {
    if (loading || next === selection) return;
    const previous = selection;
    setSelection(next);
    setAskingForNote(false);
    setThanked(false);
    if (!(await save(next))) {
      setSelection(previous);
      return;
    }
    if (next === FeedbackSelection.Negative) {
      setAskingForNote(true);
    } else {
      setThanked(true);
    }
  };

  const sendNote = async () => {
    if (loading) return;
    const text = note.trim();
    if (!text) {
      setAskingForNote(false);
      setThanked(true);
      return;
    }
    if (await save(FeedbackSelection.Negative, text)) {
      setAskingForNote(false);
      setNote("");
      setThanked(true);
    }
  };

  const thumb = (value: Feedback["selection"], label: string) => {
    const active = selection === value;
    const Icon =
      value === FeedbackSelection.Positive
        ? ThumbsUpOutlineIcon
        : ThumbsDownOutlineIcon;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={label}
            aria-pressed={active}
            onClick={() => rate(value)}
            className={cn(active && "bg-state-selected")}
          >
            <Icon
              className={cn(
                "size-3.5",
                active ? "text-icon-primary" : "text-icon-tertiary",
              )}
            />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    );
  };

  return (
    <div
      className="mt-1 flex flex-col gap-1.5"
      data-testid="opus-agent-feedback"
    >
      <div className="flex items-center gap-0.5">
        {thumb(FeedbackSelection.Positive, "Good response")}
        {thumb(FeedbackSelection.Negative, "Bad response")}
        {/* Always mounted so screen readers announce the thanks. */}
        <Text
          role="status"
          variant="fine-print-description"
          className="ml-1.5 text-text-tertiary"
        >
          {thanked ? "Thanks for the feedback" : ""}
        </Text>
      </div>
      {askingForNote && (
        <div className="flex w-full max-w-[85%] flex-col gap-1.5">
          <Textarea
            autoFocus
            value={note}
            onChange={(event) => setNote(event.target.value)}
            onKeyDown={(event) => {
              // Enter that confirms an IME composition is not a send.
              const composing =
                event.nativeEvent.isComposing || event.keyCode === 229;
              if (event.key === "Enter" && !event.shiftKey && !composing) {
                event.preventDefault();
                void sendNote();
              }
            }}
            aria-label="What went wrong?"
            placeholder="What went wrong? (optional)"
            maxLength={2000}
            className="min-h-16"
          />
          <div className="flex justify-end gap-1.5">
            <Button
              variant="ghost"
              size="xs"
              onClick={() => {
                setAskingForNote(false);
                setThanked(true);
              }}
            >
              Skip
            </Button>
            <Button
              size="xs"
              onClick={() => void sendNote()}
              disabled={loading || !note.trim()}
            >
              Send
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

gql`
  mutation SetOpusAgentTurnFeedback($input: SetOpusAgentTurnFeedbackInput!) {
    setOpusAgentTurnFeedback(input: $input) {
      success
      error {
        code
        message
      }
      turn {
        id
        myFeedback {
          id
          selection
          text
        }
      }
    }
  }
`;
